import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { UnsubscribeComponent } from '../../generals/Components';
import { formatDateR } from '../../helpers/helper';
import { CustomerAddress, CustomerEditor, CustomerGroup, LabelValue, LabelValueNumber } from '../../interfaces/interfaces';
import { COUNTRIES_LABELVALUE } from '../../const/countries.const';
import { LEGAL_STATUSES, STATUSES, STOCK_RIGHTS, VAT_TYPES } from '../../const/customers.const';
import { ConfirmComponent } from '../confirm/confirm.component';

@Component({
  selector: 'app-customereditor',
  templateUrl: './customereditor.component.html',
  styleUrls: ['./customereditor.component.scss']
})
export class CustomereditorComponent extends UnsubscribeComponent {

  customerId: number|null = null;

  countries: LabelValue[] = COUNTRIES_LABELVALUE.map((country: LabelValue) => ({
    label: country.label,
    value: country.label
  }));
  statuses: LabelValue[] = STATUSES;
  stockRights: LabelValue[] = STOCK_RIGHTS;
  vatTypes: LabelValue[] = VAT_TYPES;
  legalStatuses: LabelValue[] = LEGAL_STATUSES;

  deletedAddressIds: number[] = [];

  customerEditorAddressesFormArray: FormArray = new FormArray([]);
  customerEditorForm: FormGroup|null = null;

  dataLoading = false;
  customerEditor: CustomerEditor|null = null;
  loading = false;

  customerGroups: LabelValueNumber[] = [];

  constructor(
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private apiService: ApiService,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {
    super();
    this.init();
  }

  init(): void {
    this.initCustomerEditorForm();
    const customerId: string|null = this.activatedRoute.snapshot.paramMap.get('id');
    if (customerId !== null) {
      this.customerId = parseInt(customerId, 10);
    }
    this.loadData();
  }

  initCustomerEditorForm(): void {
    this.customerEditorForm = new FormGroup({
      status: new FormControl('NOT_ACTIVATED'),
      legalStatus: new FormControl('COMPANY'),
      email: new FormControl('', [Validators.required, Validators.email]),
      emailConfirmed: new FormControl(false),
      password: new FormControl(''),
      name: new FormControl('', [Validators.required]),
      phone: new FormControl(''),
      webpage: new FormControl('', []),
      adminNote: new FormControl('', []),
      stockRight: new FormControl('DEFAULT'),
      registrationDate: new FormControl('', []),
      newsletter: new FormControl(false),
      paymentMethods: new FormControl([], []),
      customerGroupId: new FormControl(null),
      vatNumber: new FormControl('', [Validators.required]),
      vatType: new FormControl('DOMESTIC'),
      addresses: this.customerEditorAddressesFormArray,
      deletedAddressIds: new FormControl([])
    });
    this.customerEditorForm.get('legalStatus').valueChanges.subscribe((legalStatus: string) => {
      this.customerEditorForm.get('vatNumber').clearValidators();
      if (legalStatus !== 'PRIVATE') {
        this.customerEditorForm.get('vatNumber').setValidators([Validators.required]);
      }
      this.customerEditorForm.get('legalStatus').updateValueAndValidity({emitEvent: false});
      this.customerEditorForm.updateValueAndValidity({emitEvent: false});
    });
  }

  loadData(): void {
    this.dataLoading = true;
    if (this.customerId === null) {
      combineLatest([
        this.apiService.getSimpleTable<CustomerGroup>('customergroups')
      ]).pipe(takeUntil(this.unsubscribe)).subscribe(([{data}]) => {
        this.customerGroups = [{value: null, label: 'Nincs kiválsztva'}, ...data.map((d) => ({label: d.name, value: d.id}))];
      }, () => {
        this.back();
      }).add(() => {
        this.dataLoading = false;
      });
    } else {
      combineLatest([
        this.apiService.getSimpleTable<CustomerGroup>('customergroups'),
        this.apiService.getCustomer(this.customerId)
      ]).pipe(takeUntil(this.unsubscribe)).subscribe(([{data}, {customerEditor}]) => {
        this.customerGroups = [{value: null, label: 'Nincs kiválsztva'}, ...data.map((d) => ({label: d.name, value: d.id}))];
        this.customerEditor = customerEditor;
        this.customerEditorForm.patchValue({
          ...this.customerEditor,
          registrationDate: formatDateR(this.customerEditor.registrationDate)
        });
        this.customerEditor.addresses.forEach((customerAddress: CustomerAddress) => {
          this.addCustomerAddress(customerAddress);
        });
      }, () => {
        this.back();
      }).add(() => {
        this.dataLoading = false;
      });
    }
  }

  save(): void {
    this.loading = true;
    const customerEditor: CustomerEditor = this.customerEditorForm.getRawValue();
    customerEditor.deletedAddressIds = this.deletedAddressIds;
    customerEditor.addresses.forEach((customerAddress: CustomerAddress, i) => {
      customerAddress.touched = this.customerEditorAddressesFormArray.get(i.toString()).touched;
    });
    customerEditor.registrationDate = this.customerEditor.registrationDate;
    if (this.customerId === null) {
      customerEditor.registrationDate = null;
      this.apiService.createCustomer(customerEditor).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Vásárló hozzáadása',
          detail: 'Sikeres hozzáadás'
        });
      }).add(() => {
        this.loading = false;
      })
    } else {
      this.apiService.updateCustomer(this.customerId, customerEditor).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Vásárló szerkesztése',
          detail: 'Sikeres szerkesztés'
        });
        this.back();
      }).add(() => {
        this.loading = false;
      });
    }
  }

  back(): void {
    this.location.back();
  }

  deleteCustomerAddress(index: number): void {
    this.dialogService.open(ConfirmComponent, {
      data: {
        title: 'Cím törlése',
        text: 'Biztosan törölni szeretnéd ezt a címet?',
        acceptText: 'Törlés',
        declineText: 'Mégse'
      }
    }).onClose.pipe(filter(r => r === true)).subscribe(() => {
      const value: string = this.customerEditorAddressesFormArray.get(index.toString()).get('id').value;
      if (value !== null) {
        this.deletedAddressIds.push(parseInt(value, 10));
      }
      this.customerEditorAddressesFormArray.removeAt(index);
    });
  }

  addCustomerAddress(customerAddress: CustomerAddress|null = null): void {
    const customerAddressFormGroup: FormGroup = new FormGroup({
      addressType: new FormControl('DOMESTIC'),
      country: new FormControl('Magyarország'),
      postcode: new FormControl('', [Validators.required]),
      city: new FormControl(''),
      address: new FormControl(''),
      parcelNumber: new FormControl(''),
      publicPlaceName: new FormControl(''),
      publicPlaceType: new FormControl(''),
      houseNumber: new FormControl(''),
      building: new FormControl(''),
      stairway: new FormControl(''),
      level: new FormControl(''),
      door: new FormControl(''),
      id: new FormControl(null),
      touched: new FormControl(false),
      note: new FormControl(''),
      defaultBillingAddress: new FormControl(false),
      defaultShippingAddress: new FormControl(false)
    });
    if (customerAddress !== null) {
      customerAddressFormGroup.patchValue(customerAddress);
    }
    this.customerEditorAddressesFormArray.push(customerAddressFormGroup);
  }
}
